/* global localStorage */
// Adapted from app/javascript/src/newDeliverySection.js
import { deleteFetchWrapper, getFetchWrapper, putFetchWrapper } from './fetchHelpers'
import { getLocaleFromPath } from '../utils/localeHelpers'
import { trackAmplitudeEvent } from '../analytics/CustomEvents'

let importTaxMessageOriginallyVisible = false

export default function initNewDeliverySection () {
  const deliveryQuoteLocation = document.getElementById('vjs-delivery-quote-location')

  if (deliveryQuoteLocation && deliveryQuoteLocation.value !== '') {
    toggleDeliveryEta(deliveryQuoteLocation.value)
    trackDeliveryPriceSeen('quote')
    return
  }

  if (!document.getElementById('vjs-fixed-delivery-container')) return
  if (!document.getElementById('vjs-delivery-select')) return
  if (document.querySelector('[data-id="show-delivery-quote"]')) return

  const importTaxMessage = document.getElementById('vjs-import-taxes-message')
  if (importTaxMessage) {
    importTaxMessageOriginallyVisible = !!importTaxMessage.textContent.trim()
  }

  const deliverySelect = document.getElementById('vjs-delivery-select')
  if (deliverySelect) {
    deliverySelect.addEventListener('click', () => {
      // Otherwise the saved postcode wins on the next page and the choice is lost.
      if (postcodeIsActive()) {
        removePostcode().then(() => showDeliveryPrice(true))
      } else {
        showDeliveryPrice(true)
      }
    })
  }

  const deliveryOptionsLink = document.getElementById('vjs-delivery-options-link')
  if (deliveryOptionsLink) {
    deliveryOptionsLink.addEventListener('click', () => toggleDeliveryOptions())
  }

  // Also when a postcode is showing, so Remove prices the remembered destination.
  selectStoredLocation()

  if (postcodePromptElement()) {
    initPostcodePrompt()
  } else {
    showDeliveryPrice(false)
  }
}

// ---- Postcode prompt (behind the :pdp_delivery_postcode Flipper flag) ----

function postcodePromptElement () {
  return document.getElementById('vjs-postcode-prompt')
}

function postcodeIsActive () {
  const prompt = postcodePromptElement()
  return Boolean(prompt && prompt.dataset.location)
}

function initPostcodePrompt () {
  bindPostcodeInput()
  bindPostcodeRemoveButton()

  if (postcodeIsActive()) {
    // Server rendered the filled state from the stored postcode.
    trackDeliveryPriceSeen('postcode')
    return
  }

  showDeliveryPrice(false)
}

function bindPostcodeInput () {
  const input = document.getElementById('vjs-postcode-input')
  if (!input) return

  input.addEventListener('keydown', event => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submitPostcode(input)
    }
  })
  input.addEventListener('change', () => submitPostcode(input))
}

function bindPostcodeRemoveButton () {
  const removeButton = document.getElementById('vjs-postcode-remove')
  if (!removeButton) return

  removeButton.addEventListener('click', () => {
    // Hidden straight away so a slow request doesn't invite repeat clicks.
    togglePostcodeRemoveButton(false)
    removePostcode().then(removed => {
      if (!removed) {
        togglePostcodeRemoveButton(true)
        return
      }
      trackAmplitudeEvent('PDP Postcode Removed', { 'listing id': listingId() })
      showDeliveryPrice(false)
    })
  })
}

// Resolves to whether the server removed it; the page only changes if it did.
function removePostcode () {
  return deleteFetchWrapper('/delivery_postcode')
    .then(response => {
      if (response.ok) clearPostcode()
      return response.ok
    })
    .catch(error => {
      console.log(error)
      return false
    })
}

function clearPostcode () {
  const input = document.getElementById('vjs-postcode-input')
  if (input) input.value = ''
  clearPostcodeSubmissionGuard()
  setPromptLocation('')
  togglePostcodeRemoveButton(false)
}

function togglePostcodeRemoveButton (visible) {
  const removeButton = document.getElementById('vjs-postcode-remove')
  if (removeButton) removeButton.classList.toggle('hidden', !visible)
}

function submitPostcode (input) {
  const postcode = input.value.trim()
  if (!postcode) return
  if (input.dataset.lastSubmitted === postcode) return
  input.dataset.lastSubmitted = postcode

  putFetchWrapper('/delivery_postcode', { postcode: postcode, listing_id: listingId() })
    .then(response => response.json().then(data => ({ ok: response.ok, data: data })))
    .then(result => {
      if (result.ok) {
        hidePostcodeError()
        trackPostcodeAdded(result.data)
        displayPostcodeDeliveryPrice(result.data)
      } else {
        delete input.dataset.lastSubmitted
        showPostcodeError(result.data.error)
      }
    })
    .catch(error => {
      delete input.dataset.lastSubmitted
      console.log(error)
    })
}

// The server resolves value_to_display fully (fixed-price fallback included),
// so the shared display pipeline renders it verbatim. A valid postcode with no
// priceable region (e.g. Channel Islands) arrives as location '' + 'Request a quote'.
function displayPostcodeDeliveryPrice (data) {
  const location = data.location || ''

  syncPostcodeInput(data.postcode)
  setPromptLocation(location)
  setOneWeekDestination(data.postcode)

  const deliveryPrice = data.value_to_display === 'Request a quote' ? null : data.price_in_gbp
  updateCartItemLocation(location, deliveryPrice)
  displayDeliveryPrice(location, data.postcode, data.value_to_display)
  togglePostcodeRemoveButton(Boolean(data.location))
  hideOrShowDeliveryOnDayOfChoice(data.show_delivery_on_day_of_choice)
  if (data.location) updateImportTaxMessageForDeliveryLocation(data.location)

  hidePostcodePrompt()
  closeDeliveryOptions()
  trackDeliveryPriceSeen('postcode')
}

function trackPostcodeAdded (data) {
  trackAmplitudeEvent('PDP Postcode Added', {
    postcode: data.postcode,
    'delivery location': data.location,
    'listing id': listingId()
  })
}

function listingId () {
  const listingIdElement = document.querySelector('[data-id="content-listing-id"]')
  return listingIdElement ? listingIdElement.innerHTML.trim() : null
}

function syncPostcodeInput (postcode) {
  const input = document.getElementById('vjs-postcode-input')
  if (!input) return
  input.value = postcode
  input.dataset.lastSubmitted = postcode
}

function setPromptLocation (location) {
  const prompt = postcodePromptElement()
  if (prompt) prompt.dataset.location = location
}

function setOneWeekDestination (destination) {
  const oneWeekDestination = document.getElementById('vjs-one-week-destination')
  if (oneWeekDestination) oneWeekDestination.textContent = destination
}

function resetOneWeekDestination () {
  const oneWeekDestination = document.getElementById('vjs-one-week-destination')
  if (oneWeekDestination) oneWeekDestination.textContent = oneWeekDestination.dataset.defaultDestination
}

function showPostcodeError (message) {
  const error = document.getElementById('vjs-postcode-error')
  if (!error) return
  if (message) error.textContent = message
  error.classList.remove('hidden')
}

function hidePostcodeError () {
  const error = document.getElementById('vjs-postcode-error')
  if (error) error.classList.add('hidden')
}

function hidePostcodePrompt () {
  const prompt = postcodePromptElement()
  if (prompt) prompt.classList.add('hidden')
}

function closeDeliveryOptions () {
  const deliveryOptions = document.getElementById('vjs-delivery-options')
  if (deliveryOptions && !deliveryOptions.classList.contains('hidden')) {
    toggleDeliveryOptions()
  }
}

// After a destination change lands on a UK region, invite a postcode again;
// non-UK destinations have nothing to gain from one.
function togglePostcodePromptForLocation (locationValue) {
  const prompt = postcodePromptElement()
  if (!prompt) return

  if (locationValue.includes('United Kingdom')) {
    prompt.classList.remove('hidden')
  } else {
    prompt.classList.add('hidden')
  }
}

// A destination change means re-submitting the same postcode is meaningful
// again, so drop the duplicate-submission guard.
function clearPostcodeSubmissionGuard () {
  const input = document.getElementById('vjs-postcode-input')
  if (input) delete input.dataset.lastSubmitted
}

// ---- Shared delivery price display ----

function toggleDeliveryOptions () {
  const deliveryOptions = document.getElementById('vjs-delivery-options')
  const closedChevron = document.getElementById('vjs-options-closed')
  const openChevron = document.getElementById('vjs-options-open')

  if (closedChevron && openChevron) {
    closedChevron.classList.toggle('hidden')
    openChevron.classList.toggle('hidden')
  }

  deliveryOptions.classList.toggle('hidden')
}

function toggleDeliveryEta (location) {
  const ukDeliveryEstimate = document.getElementById('vjs-estimated-delivery-time-to-uk')
  const oneWeekGuarantee = document.getElementById('vjs-one-week-guarantee')

  if (ukDeliveryEstimate) {
    if (location.includes('United Kingdom')) {
      ukDeliveryEstimate.classList.remove('hidden')
    } else {
      ukDeliveryEstimate.classList.add('hidden')
    }
  } else if (oneWeekGuarantee) {
    if (location.includes('Greater London')) {
      oneWeekGuarantee.classList.remove('hidden')
    } else {
      oneWeekGuarantee.classList.add('hidden')
    }
  }
}

function showDeliveryPrice (shouldChange) {
  // In V2, we could actually add a small data structure to the page and use
  // with this instead of depending on extra requests...
  getFetchWrapper(deliveryPriceUrl()).then((response) => response.json()).then((data) => {
    const priceToDisplay = data.value_to_display
    const gbpPrice = data.price_in_gbp
    const showDeliveryOnDayOfChoice = data.show_delivery_on_day_of_choice

    const deliverySelect = document.getElementById('vjs-delivery-select')
    const selectedLocationValue = deliverySelect.options[deliverySelect.selectedIndex].value
    const selectedLocationText = deliverySelect.options[deliverySelect.selectedIndex].text

    if (shouldChange) {
      localStorage.setItem('SelectedLocationIndex', deliverySelect.selectedIndex)
    }

    const deliveryPrice = priceToDisplay === 'Request a quote' ? null : gbpPrice
    updateCartItemLocation(selectedLocationValue, deliveryPrice)

    const isEstimate = isEstimatedLocation(selectedLocationValue)
    displayDeliveryPrice(selectedLocationValue, selectedLocationText, priceToDisplay, isEstimate)
    hideOrShowDeliveryOnDayOfChoice(showDeliveryOnDayOfChoice)
    updateImportTaxMessageForDeliveryLocation(selectedLocationValue)
    setPromptLocation('')
    resetOneWeekDestination()
    clearPostcodeSubmissionGuard()
    togglePostcodePromptForLocation(selectedLocationValue)
    if (postcodePromptElement()) closeDeliveryOptions()
    trackDeliveryPriceSeen(isEstimate ? 'estimate' : 'destination')
  }).catch((error) => console.log(error))
}

// UK region prices are shown as an estimate without naming the region; the
// postcode prompt underneath makes them accurate.
function isEstimatedLocation (locationValue) {
  return Boolean(postcodePromptElement()) && locationValue.includes('United Kingdom')
}

function updateDeliveryLabel (isEstimate, requestsAQuote) {
  const label = document.getElementById('vjs-delivery-label')
  if (!label) return

  if (!isEstimate) {
    label.textContent = label.dataset.deliveryTo
  } else if (requestsAQuote) {
    label.textContent = label.dataset.delivery
  } else {
    label.textContent = label.dataset.estimatedDelivery
  }
}

let lastTrackedDeliveryPrice = null

function trackDeliveryPriceSeen (priceType) {
  const price = displayedDeliveryPrice()
  if (!price) return

  const selectedLocation = displayedDeliveryLocation()

  const payload = JSON.stringify([price, selectedLocation])
  if (payload === lastTrackedDeliveryPrice) return
  lastTrackedDeliveryPrice = payload

  if (window.dataLayer) window.dataLayer.push({ amplitude_event_properties: null })

  trackAmplitudeEvent('PDP Delivery Price Seen', {
    'delivery price': numericPrice(price),
    'selected location': selectedLocation,
    'price type': priceType,
    'listing id': listingId()
  })
}

function numericPrice (displayValue) {
  const numeric = parseFloat(displayValue.replace(/[^0-9.]/g, ''))
  return isNaN(numeric) ? displayValue : numeric
}

function displayedDeliveryPrice () {
  const priceElements = [
    document.getElementById('vjs-delivery-section_delivery-quote'),
    document.getElementById('vjs-delivery-free'),
    document.getElementById('vjs-delivery-section_price'),
    document.getElementById('vjs-uk-delivery-price')
  ]
  const visiblePrice = priceElements.find(el => isRendered(el) && el.textContent.trim() !== '')
  if (visiblePrice) return visiblePrice.textContent.trim()

  const requestQuoteLink = document.getElementById('vjs-international-delivery')
  if (isRendered(requestQuoteLink)) return 'Request a quote'

  return null
}

function isRendered (el) {
  return Boolean(el && el.getClientRects().length > 0)
}

function displayedDeliveryLocation () {
  const quoteLocation = document.getElementById('vjs-delivery-quote-location')
  if (quoteLocation && quoteLocation.value !== '') return quoteLocation.value

  if (postcodeIsActive()) return postcodePromptElement().dataset.location

  const deliverySelect = document.getElementById('vjs-delivery-select')
  return deliverySelect ? deliverySelect.options[deliverySelect.selectedIndex].text : null
}

function hideOrShowDeliveryOnDayOfChoice (showDeliveryOnDayOfChoice) {
  const deliveryOnDayOfChoiceMessages = Array.from(document.getElementsByClassName('vjs-delivery-on-day-of-choice'))
  deliveryOnDayOfChoiceMessages.forEach(deliveryOnDayOfChoice => {
    if (showDeliveryOnDayOfChoice) {
      deliveryOnDayOfChoice.classList.remove('hidden')
    } else {
      deliveryOnDayOfChoice.classList.add('hidden')
    }
  })
}

function deliveryPriceUrl () {
  const location = document.getElementById('vjs-delivery-select').value
  const listingIdElement = document.querySelector('[data-id="content-listing-id"]')

  if (!location || !listingIdElement) return
  return '/delivery_prices/' + listingIdElement.innerHTML + '/price_by_location/' + location
}

function updateCartItemLocation (location, amount) {
  const deliveryLocation = document.getElementById('vjs-cart-delivery-location')
  const deliveryPrice = document.getElementById('vjs-cart-delivery-price')
  const itemIsOutOfCart = Boolean(deliveryLocation && deliveryPrice)

  if (itemIsOutOfCart) {
    deliveryLocation.value = location
    deliveryPrice.value = amount
  }
}

function displayFreeDelivery () {
  const requestDeliveryPrice = document.getElementById('vjs-international-delivery')
  const ukDeliveryPrice = document.getElementById('vjs-uk-delivery-price')
  const deliveryPriceSection = document.getElementById('vjs-delivery-section_price')
  const forLabel = document.getElementById('vjs-for-label')

  if (forLabel) forLabel.classList.remove('hidden')
  if (ukDeliveryPrice) ukDeliveryPrice.style.display = 'none'
  if (requestDeliveryPrice) requestDeliveryPrice.classList.add('hidden')
  if (deliveryPriceSection) deliveryPriceSection.style.display = 'none'

  showFreeTag()
  showDeliverySection()
}

function displayRequestAQuote () {
  const deliveryPriceContainer = document.getElementById('vjs-uk-delivery-price')
  const deliveryPriceSection = document.getElementById('vjs-delivery-section_price')
  const requestDeliveryPrice = document.getElementById('vjs-international-delivery')
  const forLabel = document.getElementById('vjs-for-label')

  if (forLabel) forLabel.classList.add('hidden')

  if (deliveryPriceContainer) {
    deliveryPriceContainer.classList.add('hidden')
    deliveryPriceSection.classList.add('hidden')
    deliveryPriceSection.style.display = ''
  }
  if (requestDeliveryPrice) { requestDeliveryPrice.classList.remove('hidden') }
}

function displayPrice (value) {
  const requestDeliveryPrice = document.getElementById('vjs-international-delivery')
  const ukDeliveryPrice = document.getElementById('vjs-uk-delivery-price')
  const price = document.getElementById('vjs-delivery-section_price')
  const forLabel = document.getElementById('vjs-for-label')

  if (forLabel) forLabel.classList.remove('hidden')
  if (ukDeliveryPrice) ukDeliveryPrice.style.display = 'none'
  if (requestDeliveryPrice) requestDeliveryPrice.classList.add('hidden')

  if (price) {
    price.style.display = 'inline'
    if (value.endsWith('.00')) {
      // Make it nicer by trimming cents
      price.textContent = value.slice(0, -3)
    } else {
      price.textContent = value
    }
  }
  showDeliverySection()
}

function displayDeliveryPrice (selectedLocationValue, selectedLocationText, deliveryPrice, isEstimate) {
  const fixedDeliveryPrice = document.getElementById('vjs-delivery-fixed')
  const selectedLocationContainer = document.getElementById('vjs-delivery-selected-location')
  const isUkMainland = selectedLocationValue.includes('United Kingdom') && !selectedLocationValue.includes('Northern Ireland')
  const fixedPriceApplies = isUkMainland && fixedDeliveryPrice.value !== 'Request a quote'
  const priceToShow = deliveryPrice || (fixedPriceApplies ? fixedDeliveryPrice.value : 'Request a quote')

  selectedLocationContainer.innerText = isEstimate ? '' : selectedLocationText
  updateDeliveryLabel(isEstimate, priceToShow === 'Request a quote')
  toggleDeliveryEta(selectedLocationValue)
  hideFreeTag()

  if (priceToShow === 'Free') {
    displayFreeDelivery()
  } else if (priceToShow === 'Request a quote' && document.getElementById('vjs-international-delivery')) {
    displayRequestAQuote()
  } else {
    displayPrice(priceToShow)
  }

  showDeliverySection()
}

function storedOrDefaultLocationIndex () {
  const storedLocationIndex = localStorage.getItem('SelectedLocationIndex')
  if (storedLocationIndex !== null) return storedLocationIndex

  const locale = getLocaleFromPath(window.location.pathname)
  // On a non-default locale, default the delivery destination to that locale's
  // country (from the Locale registry via <body data-locale-delivery-location>)
  // when the buyer hasn't chosen one yet.
  if (locale !== null) {
    const localeLocationIndex = localeDefaultDeliveryLocationIndex()
    if (localeLocationIndex !== null) {
      localStorage.setItem('SelectedLocationIndex', localeLocationIndex)
      return localeLocationIndex
    }
  }

  return null
}

// Index of the <option> matching the current locale's default delivery location,
// or null if there's no such option. Matches on the option value (the English
// location key, e.g. 'Netherlands'), so it's locale-agnostic.
function localeDefaultDeliveryLocationIndex () {
  const location = document.body?.dataset?.localeDeliveryLocation
  if (!location) return null
  const select = document.getElementById('vjs-delivery-select')
  if (!select) return null
  const option = Array.from(select.options).find((o) => o.value === location)
  return option ? String(option.index) : null
}

function showDeliverySection () {
  const section = document.getElementById('vjs-delivery-section')

  section.classList.remove('invisible')
}

function showFreeTag () {
  const freeTag = document.getElementById('vjs-delivery-free')
  if (freeTag) {
    freeTag.classList.add('vsd-badge__success')
    freeTag.classList.remove('hidden')
  }
}

function hideFreeTag () {
  const freeTag = document.getElementById('vjs-delivery-free')
  if (freeTag) {
    freeTag.classList.remove('vsd-badge__success')
    freeTag.classList.add('hidden')
  }
}

function isEuropeanDeliveryLocation (location) {
  return !location.startsWith('United Kingdom') &&
    location !== 'United States' &&
    location !== 'Rest of the world'
}

function updateImportTaxMessageForDeliveryLocation (selectedLocationValue) {
  const importTaxMessage = document.getElementById('vjs-import-taxes-message')
  if (!importTaxMessage) return

  const sellerInEu = !!document.querySelector('[data-id="seller-location-in-eu"]')
  if (!sellerInEu) return

  if (isEuropeanDeliveryLocation(selectedLocationValue)) {
    importTaxMessage.hidden = true
  } else {
    importTaxMessage.hidden = !importTaxMessageOriginallyVisible
  }
}

// No click: the dropdown's click handler treats a click as the buyer's choice,
// saving it and removing their postcode. Runs before customSelect builds the
// dropdown, which then shows this selection.
function selectStoredLocation () {
  const sessionSelectedLocation = storedOrDefaultLocationIndex()
  if (sessionSelectedLocation === null) return

  const deliverySelect = document.getElementById('vjs-delivery-select')
  deliverySelect.selectedIndex = sessionSelectedLocation

  if (deliverySelect.selectedIndex === -1) {
    deliverySelect.selectedIndex = 0
  }
}
